/**
 * ReservationsGrid — the WIRED reservations list body (SCRUM-334, Step 7).
 *
 * Replaces the SCRUM-333 `ReservationsPage` `NotConnectedYet` shell with the
 * real, data-connected grid. It is a `modules/booking` feature (a
 * user-facing action surface — filtering + row → detail navigation), NOT a
 * cross-BC widget: the only cross-slice touch is the sanctioned
 * barrel-only `usePropertyNameLookup()` reference lookup from the `property`
 * slice (a thin `id → name` map, not a composition).
 *
 * **Composition (never refetches / re-maps here).**
 *   - `useReservationsList({ status, limit: 100 })` — the working-set rows.
 *     `status` is pushed SERVER-side via the hook input (spends the ≤100-row
 *     page budget on relevant-status rows); `search` + `type` are applied
 *     CLIENT-side over the returned page (all three compose AND). This is
 *     the PRIMARY state source (loading / ready / forbidden / error).
 *   - `useReservationTypes()` — the type-filter catalog (AUXILIARY): if it is
 *     forbidden/error the grid still renders with the type filter reduced to
 *     "All types" (never hard-blocks the grid).
 *   - `usePropertyNameLookup()` (property barrel) — the `id → name` map
 *     (AUXILIARY): rows fall back to a short property id when a name is not
 *     yet resolved.
 *   - `toReservationRowView(dto, nameById)` — the pure per-row view (nights,
 *     modified, glyphKind, sourceLabel, propertyName, Cents passthrough).
 *
 * **Money is `Cents`.** Totals/balances render through `formatCents(cents,
 * intl)` at the display boundary (the LedgerLine precedent) — never a
 * hand-rolled `/100`. A `null` legacy total/balance renders a neutral
 * placeholder, NOT `formatCents(0)`. Like `LedgerLine`, this component only
 * calls `useIntl()` and expects an `IntlProvider` from above — the app root
 * (`app/providers.tsx`) supplies it at runtime; specs wrap in
 * `AppIntlProvider` (the LedgerLine.spec pattern). The component never
 * self-mounts a provider.
 *
 * **Tokens (ADR frontend-0003, SCRUM-78/79).** Semantic `var(--token)` +
 * Tailwind branded scales only; no raw hex/rgba, no inline-style numeric
 * literals, no pixel-literal arbitrary values. Mirrors `PropertiesPage`.
 *
 * **A11y (WCAG 2.1 AA, FE rule 7).** Semantic table/list, keyboard-activatable
 * rows (Enter/Space), aria-live for the state/result-count changes, ≥44px
 * touch targets on interactive controls, status conveyed by text (never
 * color alone).
 *
 * **NEED (honest omission).** No health-dot column — there is no health
 * signal source (`HealthAgent` is an honest shell). No fake dot.
 */

import * as React from 'react';
import { useIntl } from 'react-intl';
import { RESERVATION_STATUS_WIRE_VALUES } from '@artemis/validators';
import { Badge, Button, Card, Icon } from '@sparta/ui-kit';
import { formatCents } from '../../../shared/formatters/entity-change/values.js';
import { useReservationsList } from '../api/use-reservations-list.js';
import { useReservationTypes } from '../api/use-reservation-types.js';
import { toReservationRowView, type ReservationRowView } from '../api/to-reservation-row-view.js';
import type { ReservationListItemDto } from '../api/types.js';
import { usePropertyNameLookup } from '../../../modules/property/index.js';
import { ResStatusPill, statusLabelFor } from '../_components/ResStatusPill.js';
import { TypeIconBadge } from '../_components/TypeIconBadge.js';

/**
 * The sentinel filter id for the "All types" default (no type constraint) and
 * the "Untyped" bucket (`reservationTypeKey === null`). Real catalog keys are
 * opaque wire strings, so these two FE-only sentinels are non-colliding
 * symbols in a discriminated shape (never a wire key).
 */
const ALL_TYPES = '__all_types__' as const;
const UNTYPED = '__untyped__' as const;
type TypeFilterValue = typeof ALL_TYPES | typeof UNTYPED | string;

/**
 * The sentinel filter id for the "All statuses" default (no status
 * constraint). Real wire statuses are the closed
 * `RESERVATION_STATUS_WIRE_VALUES` set, so this FE-only sentinel is a
 * non-colliding symbol (never a wire status). Unlike the type filter, the
 * status filter is applied SERVER-side (passed to `useReservationsList`), so
 * exactly one status is selected at a time; "All statuses" clears the param.
 */
const ALL_STATUSES = '__all_statuses__' as const;
type StatusFilterValue = typeof ALL_STATUSES | string;

/** The page cap — the single bounded fetch size (mirrors the wire max). */
const PAGE_CAP = 100;

export interface ReservationsGridProps {
  /** Row activation → open the reservation detail (`/reservations/:id`). */
  readonly onOpen?: (id: string) => void;
  /** "New reservation" affordance → open the create surface. */
  readonly onCreate?: () => void;
  /**
   * "Record channel reservation" affordance → open the manual channel-ingest
   * surface (`/reservations/record-channel`). Rendered ONLY when
   * `canRecordChannel` is `true` AND this callback is provided.
   */
  readonly onRecordChannel?: () => void;
  /**
   * Whether the current user may record a channel reservation. The grid is
   * presentational — the OWNING page computes this (SCRUM-350: the button
   * requires `booking.reservations.ingest` + `booking.reservations.read` +
   * `property.read` together, since the manual channel-ingest surface would
   * dead-end on a 403-riddled page for a partial-permission role). Defaults to
   * `false`, so the button is opt-in and never leaks without an explicit grant.
   */
  readonly canRecordChannel?: boolean;
  /** Denser layout (tighter paddings) — for embedded surfaces (dashboard). */
  readonly compact?: boolean;
}

export function ReservationsGrid({
  onOpen,
  onCreate,
  onRecordChannel,
  canRecordChannel = false,
  compact = false,
}: ReservationsGridProps): React.ReactElement {
  const [search, setSearch] = React.useState('');
  const [typeFilter, setTypeFilter] = React.useState<TypeFilterValue>(ALL_TYPES);
  const [statusFilter, setStatusFilter] = React.useState<StatusFilterValue>(ALL_STATUSES);

  // PRIMARY state source. `status` is pushed SERVER-side via the hook input
  // (spends the ≤100-row page budget on relevant-status rows); "All statuses"
  // clears the param so the whole readable page is the working set. `search`
  // + `type` are applied CLIENT-side over the returned page below, so all
  // three filters compose AND.
  const listResult = useReservationsList({
    status: statusFilter === ALL_STATUSES ? undefined : [statusFilter],
    limit: PAGE_CAP,
  });
  // AUXILIARY sources — degrade gracefully, never hard-block the grid.
  const typesResult = useReservationTypes();
  const nameLookup = usePropertyNameLookup();

  const nameById = React.useMemo<ReadonlyMap<string, string>>(
    () => (nameLookup.status === 'ready' ? nameLookup.nameById : new Map()),
    [nameLookup],
  );

  const rows = React.useMemo<readonly ReservationRowView[]>(() => {
    if (listResult.status !== 'ready') return [];
    return listResult.data.map((dto: ReservationListItemDto) =>
      toReservationRowView(dto, nameById),
    );
  }, [listResult, nameById]);

  const filteredRows = React.useMemo<readonly ReservationRowView[]>(() => {
    const needle = search.trim().toLowerCase();
    return rows.filter((row) => {
      // type filter (client): "All types" passes everything; "Untyped"
      // matches null reservationTypeKey; otherwise exact key match.
      if (typeFilter === UNTYPED) {
        if (row.reservationTypeKey !== null) return false;
      } else if (typeFilter !== ALL_TYPES) {
        if (row.reservationTypeKey !== typeFilter) return false;
      }
      // search filter (client): guest name / id / resolved property name.
      if (needle.length > 0) {
        const haystack = [row.guestDisplayName ?? '', row.id, row.propertyName]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      return true;
    });
  }, [rows, search, typeFilter]);

  const loadedCount = rows.length;
  const filteredCount = filteredRows.length;
  const atCap = listResult.status === 'ready' && loadedCount >= PAGE_CAP;

  // Design reservations.jsx:68 — 32/40/80 desktop, 24/16/80 compact. 40px
  // side gutters (sm:px-10) + an 80px bottom gutter (pb-20) on both densities.
  const paddingClass = compact
    ? 'px-4 pt-4 pb-20'
    : 'px-4 pt-6 pb-20 sm:px-10 sm:pt-8';

  return (
    <div className={`mx-auto w-full max-w-7xl ${paddingClass}`}>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="mb-1 text-xs font-bold uppercase tracking-[0.12em] text-[color:var(--fg-tertiary)]">
            Bookings
          </p>
          <h1 className="m-0 text-2xl font-semibold tracking-[-0.025em] text-[color:var(--fg-primary)]">
            Reservations
          </h1>
          <p className="m-0 mt-1.5 text-sm text-[color:var(--fg-secondary)]" aria-live="polite">
            {listResult.status === 'ready'
              ? `${loadedCount} ${loadedCount === 1 ? 'reservation' : 'reservations'} · type, status & financial health at a glance`
              : ' '}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canRecordChannel && onRecordChannel ? (
            <Button variant="secondary" onClick={() => onRecordChannel()}>
              <Icon name="download" size={16} aria-hidden />
              <span className="ml-2">Record channel reservation</span>
            </Button>
          ) : null}
          <Button variant="primary" onClick={() => onCreate?.()}>
            <Icon name="plus" size={16} aria-hidden />
            <span className="ml-2">New reservation</span>
          </Button>
        </div>
      </header>

      <FilterBar
        search={search}
        onSearchChange={setSearch}
        typeFilter={typeFilter}
        onTypeChange={setTypeFilter}
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        typesResult={typesResult}
        counterLabel={listResult.status === 'ready' ? `${filteredCount} of ${loadedCount}` : null}
      />

      {atCap ? (
        <p
          role="status"
          aria-live="polite"
          className="mb-3 flex items-center gap-2 text-sm text-[color:var(--fg-tertiary)]"
        >
          <Icon name="info" size={14} aria-hidden />
          Showing the first {PAGE_CAP} reservations — refine by status to narrow the list.
        </p>
      ) : null}

      <GridStateBody listResult={listResult} rows={filteredRows} onOpen={onOpen} />
    </div>
  );
}

/* ── filter bar ──────────────────────────────────────────────────────────── */

interface FilterBarProps {
  readonly search: string;
  readonly onSearchChange: (next: string) => void;
  readonly typeFilter: TypeFilterValue;
  readonly onTypeChange: (next: TypeFilterValue) => void;
  readonly statusFilter: StatusFilterValue;
  readonly onStatusChange: (next: StatusFilterValue) => void;
  readonly typesResult: ReturnType<typeof useReservationTypes>;
  /** Right-aligned "{filtered} of {loaded}" result counter; null hides it (pre-ready). */
  readonly counterLabel: string | null;
}

function FilterBar({
  search,
  onSearchChange,
  typeFilter,
  onTypeChange,
  statusFilter,
  onStatusChange,
  typesResult,
  counterLabel,
}: FilterBarProps): React.ReactElement {
  // AUXILIARY: when the catalog is forbidden/error/loading, the type filter
  // degrades to "All types" + "Untyped" only (never hard-blocks the grid).
  const catalog = typesResult.status === 'ready' ? typesResult.data : [];

  const typeOptions: ReadonlyArray<{
    readonly id: TypeFilterValue;
    readonly label: string;
  }> = React.useMemo(() => {
    const catalogOptions = catalog.map((entry) => ({
      id: entry.internalKey as TypeFilterValue,
      // displayName ONLY — never the raw internalKey (no internal
      // identifiers in user copy).
      label: entry.displayName,
    }));
    return [
      { id: ALL_TYPES, label: 'All types' },
      ...catalogOptions,
      { id: UNTYPED, label: 'Untyped' },
    ];
  }, [catalog]);

  // Status options = "All statuses" + each closed wire status rendered with
  // its HUMAN label via `statusLabelFor` — the SINGLE status-copy source
  // shared with `ResStatusPill` (no second label map; no raw wire codes in
  // user copy — CLAUDE.md "no internal identifiers in user copy").
  const statusOptions: ReadonlyArray<{
    readonly id: StatusFilterValue;
    readonly label: string;
  }> = React.useMemo(
    () => [
      { id: ALL_STATUSES, label: 'All statuses' },
      ...RESERVATION_STATUS_WIRE_VALUES.map((wire) => ({
        id: wire as StatusFilterValue,
        label: statusLabelFor(wire),
      })),
    ],
    [],
  );

  return (
    <Card padding="none" className="mb-4">
      <div className="flex flex-wrap items-center gap-2.5 p-4">
        <label className="inline-flex h-11 min-w-60 max-w-sm flex-1 items-center gap-2 rounded-full border border-[var(--border-default)] bg-[var(--bg-surface)] px-3.5">
          <Icon name="search" size={14} aria-hidden className="text-[color:var(--fg-tertiary)]" />
          {/* eslint-disable-next-line @sparta/no-hand-rolled-input -- SCRUM-334: compact inline filter searchbox inside an icon pill — kit's <TextField> renders a full labelled form field (visible <label> + mb-4 block) that breaks the single-line filter row; mirrors PropertiesPage's identical hand-rolled search input. */}
          <input
            type="search"
            aria-label="Search reservations by guest, ID, or property"
            placeholder="Search guest, ID, property"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="min-w-0 flex-1 border-none bg-transparent font-[inherit] text-sm text-[color:var(--fg-primary)] outline-none"
          />
        </label>

        <FilterSelect
          label="Filter by reservation type"
          value={typeFilter}
          onChange={(next) => onTypeChange(next as TypeFilterValue)}
          options={typeOptions}
        />

        <FilterSelect
          label="Filter by reservation status"
          value={statusFilter}
          onChange={(next) => onStatusChange(next as StatusFilterValue)}
          options={statusOptions}
        />

        {counterLabel !== null ? (
          <p
            role="status"
            aria-live="polite"
            className="ml-auto text-sm text-[color:var(--fg-tertiary)]"
          >
            {counterLabel}
          </p>
        ) : null}
      </div>
    </Card>
  );
}

/**
 * A single-select filter dropdown — the shared affordance for BOTH the status
 * and type filters (the design's `RsvSelect`): a native `<select>` styled as a
 * rounded pill with a `chevDown` affordance. A native control is deliberate —
 * the ui-kit ships no Select primitive, and a native `<select>` gives correct
 * keyboard + screen-reader semantics for free (implicit `role="combobox"`, an
 * accessible name from `aria-label`) with a ≥44px touch target via `h-11`.
 * Branded-scale tokens only; the native picker arrow is suppressed
 * (`appearance-none`) in favour of the kit chevron for cross-portal parity.
 */
function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly options: ReadonlyArray<{ readonly id: string; readonly label: string }>;
}): React.ReactElement {
  return (
    <div className="relative">
      <select
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-11 cursor-pointer appearance-none rounded-full border border-[var(--border-default)] bg-[var(--bg-surface)] py-0 pl-3.5 pr-9 text-sm text-[color:var(--fg-primary)]"
      >
        {options.map((opt) => (
          <option key={opt.id} value={opt.id}>
            {opt.label}
          </option>
        ))}
      </select>
      <Icon
        name="chevDown"
        size={12}
        aria-hidden
        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--fg-tertiary)]"
      />
    </div>
  );
}

/* ── state body (loading / forbidden / error / empty / rows) ─────────────── */

interface GridStateBodyProps {
  readonly listResult: ReturnType<typeof useReservationsList>;
  readonly rows: readonly ReservationRowView[];
  readonly onOpen?: (id: string) => void;
}

function GridStateBody({ listResult, rows, onOpen }: GridStateBodyProps): React.ReactElement {
  if (listResult.status === 'loading') {
    return <LoadingSkeleton />;
  }
  if (listResult.status === 'forbidden') {
    return <ForbiddenPanel />;
  }
  if (listResult.status === 'error') {
    return <ErrorPanel onRetry={listResult.refetch} />;
  }
  if (rows.length === 0) {
    return <EmptyNoMatches />;
  }
  return <ReservationsBody rows={rows} onOpen={onOpen} />;
}

function LoadingSkeleton(): React.ReactElement {
  return (
    <Card padding="none">
      <ul aria-busy="true" aria-live="polite" data-testid="skeleton">
        {[0, 1, 2, 3].map((i) => (
          <li
            key={i}
            className={`flex items-center gap-3 px-4 py-3.5${
              i > 0 ? ' border-t border-[var(--border-subtle)]' : ''
            }`}
          >
            <span
              className="inline-block h-10 w-10 rounded-md bg-[var(--bg-sunken)]"
              aria-hidden="true"
            />
            <span
              className="inline-block h-3.5 w-48 rounded bg-[var(--bg-sunken)]"
              aria-hidden="true"
            />
          </li>
        ))}
      </ul>
      <span className="sr-only">Loading reservations…</span>
    </Card>
  );
}

function ForbiddenPanel(): React.ReactElement {
  return (
    <Card padding="lg" className="text-center">
      <h2 className="mb-1 text-base font-semibold text-[color:var(--fg-primary)]">
        You don’t have access to Reservations.
      </h2>
      <p className="text-sm text-[color:var(--fg-secondary)]">
        Ask an administrator to grant you the required permission.
      </p>
    </Card>
  );
}

function ErrorPanel({ onRetry }: { readonly onRetry: () => void }): React.ReactElement {
  return (
    <Card
      padding="lg"
      className="flex items-center justify-between gap-4"
      role="alert"
      aria-live="assertive"
    >
      <div>
        <h2 className="mb-1 text-base font-semibold text-[color:var(--fg-primary)]">
          Couldn’t load reservations.
        </h2>
        <p className="text-sm text-[color:var(--fg-secondary)]">
          Check your connection and try again.
        </p>
      </div>
      <Button variant="secondary" onClick={onRetry}>
        Retry
      </Button>
    </Card>
  );
}

function EmptyNoMatches(): React.ReactElement {
  return (
    <Card padding="lg" className="text-center">
      <h2 className="mb-1 text-base font-semibold text-[color:var(--fg-primary)]">
        No reservations match.
      </h2>
      <p className="text-sm text-[color:var(--fg-secondary)]">
        Try a different search or clear the type filter.
      </p>
    </Card>
  );
}

/* ── data body: desktop ARIA table + mobile card list (design parity) ─────── */
//
// The design (design_handoff_reservations/source/reservations.jsx) ships TWO
// distinct renderings that we reproduce faithfully rather than force one node
// to be both:
//   - `lg+` : an ARIA `table` (role table / rowgroup / row / columnheader /
//     cell) with the design's column order Type · Status · Guest · Stay ·
//     Total · Balance · Processor · chevron. Health-dot column omitted (honest
//     NEED — no backend health source).
//   - `<lg` : a stacked list of rounded per-row CARDS (jsx 90-104): line 1 =
//     type badge + guest + mono id; line 2 = status pill + a terse muted
//     "· dates · $total · $X due" summary. This is NOT the desktop cells
//     stacked — it is the design's compact card content.
// Both subtrees exist in the DOM (CSS toggles visibility); tests scope row /
// text assertions to the desktop `role="table"` so the duplicated card text
// never double-matches.

interface ReservationsBodyProps {
  readonly rows: readonly ReservationRowView[];
  readonly onOpen?: (id: string) => void;
}

function ReservationsBody({ rows, onOpen }: ReservationsBodyProps): React.ReactElement {
  return (
    <>
      {/* Desktop — the ARIA table (lg and up). */}
      <Card padding="none" className="hidden max-w-full lg:block">
        <div role="table" aria-label="Reservations" className="w-full text-sm">
          <div role="rowgroup">
            <div
              role="row"
              className="flex items-center gap-3 bg-[var(--bg-sunken)] px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.08em] text-[color:var(--fg-tertiary)]"
            >
              <span role="columnheader" className="w-10">
                Type
              </span>
              <span role="columnheader" className="w-28">
                Status
              </span>
              <span role="columnheader" className="flex-1">
                Guest
              </span>
              <span role="columnheader" className="w-40">
                Stay
              </span>
              <span role="columnheader" className="w-28 text-right">
                Total
              </span>
              <span role="columnheader" className="w-28 text-right">
                Balance
              </span>
              <span role="columnheader" className="w-28">
                Processor
              </span>
              {/* Chevron affordance column — a decorative spacer, NOT a data
                  column: kept out of the a11y table (no empty column header). */}
              <span aria-hidden className="w-6" />
            </div>
          </div>

          <div role="rowgroup">
            {rows.map((row) => (
              <ReservationRow key={row.id} row={row} onOpen={onOpen} />
            ))}
          </div>
        </div>
      </Card>

      {/* Mobile + tablet — the design's compact card list (below lg; a 7-column
          money table needs ≥lg to breathe, and the epic treats ≤899px as mobile). */}
      <ul aria-label="Reservations" className="m-0 flex list-none flex-col gap-2.5 p-0 lg:hidden">
        {rows.map((row) => (
          <li key={row.id}>
            <ReservationCard row={row} onOpen={onOpen} />
          </li>
        ))}
      </ul>
    </>
  );
}

/** Right-aligned mono money cell — neutral placeholder for a null Cents. */
function MoneyValue({
  cents,
  danger = false,
}: {
  readonly cents: number | null;
  readonly danger?: boolean;
}): React.ReactElement {
  const intl = useIntl();
  if (cents === null) {
    return (
      <span className="tabular-nums text-[color:var(--fg-tertiary)]" aria-label="Not available">
        —
      </span>
    );
  }
  // Sign inspection is a comparison, not money arithmetic — safe.
  const isDanger = danger && Number.isFinite(cents) && cents > 0;
  return (
    <span
      // ADR frontend-0002 exemption 1: color is computed — a positive balance
      // with `danger` set paints the alert token, everything else the primary
      // foreground.
      style={{
        color: isDanger ? 'var(--danger-500)' : 'var(--fg-primary)',
      }}
      className="tabular-nums"
    >
      {formatCents(cents, intl)}
    </span>
  );
}

function StayCell({ row }: { readonly row: ReservationRowView }): React.ReactElement {
  const intl = useIntl();
  // Calendar LocalDates (`YYYY-MM-DD`) → "Jun 12" (MON DD), design parity.
  // `timeZone: 'UTC'` is load-bearing: a date-only string parses to UTC
  // midnight, so formatting in any other zone would shift the day backward
  // (off-by-one). En-dash separator, no spaces (design `{arrive}–{depart}`).
  const fmt = (iso: string): string =>
    intl.formatDate(iso, { month: 'short', day: '2-digit', timeZone: 'UTC' });
  return (
    <div className="min-w-0">
      <p className="m-0 text-sm text-[color:var(--fg-primary)]">
        {fmt(row.arrivalDate)}–{fmt(row.departureDate)}
      </p>
      <p className="m-0 text-xs text-[color:var(--fg-tertiary)]">
        {row.nights} {row.nights === 1 ? 'night' : 'nights'}
      </p>
    </div>
  );
}

function SourceLabel({ row }: { readonly row: ReservationRowView }): React.ReactElement {
  return <Badge variant="neutral">{row.sourceLabel}</Badge>;
}

interface RowProps {
  readonly row: ReservationRowView;
  readonly onOpen?: (id: string) => void;
}

/**
 * One DESKTOP table row (`lg+`). A single flex node whose `role="cell"` order
 * mirrors the columnheaders (Type · Status · Guest · Stay · Total · Balance ·
 * Processor · chevron). Keyboard-activatable (Enter/Space) per the
 * PropertiesPage precedent; carries `data-reservation-id` for row lookup. The
 * mobile presentation is a separate `<ReservationCard>` (design parity), so
 * this node no longer reflows.
 */
function ReservationRow({ row, onOpen }: RowProps): React.ReactElement {
  const activate = (): void => onOpen?.(row.id);
  return (
    <div
      role="row"
      data-reservation-id={row.id}
      onClick={activate}
      tabIndex={0}
      aria-label={`Open reservation for ${row.guestDisplayName ?? 'guest with no name on file'}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          activate();
        }
      }}
      className="flex max-w-full cursor-pointer items-center gap-3 border-t border-[var(--border-subtle)] px-4 py-3.5 transition-colors hover:bg-[var(--bg-sunken)]"
    >
      <span role="cell" className="flex w-10 shrink-0 items-center">
        <TypeIconBadge
          stayKind={glyphStayKindFor(row.glyphKind)}
          reservationTypeKey={row.reservationTypeKey}
          bookingChannel={bookingChannelFor(row.glyphKind)}
        />
      </span>

      <span role="cell" className="flex w-28 shrink-0 items-center">
        <ResStatusPill status={row.status} modified={row.modified} />
      </span>

      <span role="cell" className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold text-[color:var(--fg-primary)]">
          {row.guestDisplayName ?? 'No name on file'}
        </span>
        <span className="block truncate text-xs text-[color:var(--fg-secondary)]">
          {row.propertyName}
        </span>
      </span>

      <span role="cell" className="w-40">
        <StayCell row={row} />
      </span>

      <span role="cell" className="flex w-28 justify-end">
        <MoneyValue cents={row.grandTotalCents} />
      </span>

      <span role="cell" className="flex w-28 justify-end">
        <MoneyValue cents={row.balanceDueCents} danger />
      </span>

      <span role="cell" className="w-28">
        <SourceLabel row={row} />
      </span>

      {/* Open-detail chevron (design parity) — decorative; the whole row is
          activatable, so the glyph is aria-hidden and NOT a data cell. */}
      <span aria-hidden className="flex w-6 shrink-0 items-center justify-end">
        <Icon name="chevRight" size={16} aria-hidden className="text-[color:var(--fg-tertiary)]" />
      </span>
    </div>
  );
}

/**
 * One MOBILE reservation card (`<lg`) — the design's compact 2-line card
 * (reservations.jsx 90-104), health dot omitted (honest NEED). Line 1: type
 * badge + guest + mono reservation id. Line 2: status pill + a terse muted
 * "· dates · $total · $X due" summary. A native `<button>` so the whole card
 * is one keyboard-activatable target.
 */
function ReservationCard({ row, onOpen }: RowProps): React.ReactElement {
  const intl = useIntl();
  const fmtDate = (iso: string): string =>
    intl.formatDate(iso, { month: 'short', day: '2-digit', timeZone: 'UTC' });
  const due = row.balanceDueCents;
  return (
    // eslint-disable-next-line @sparta/no-hand-rolled-button -- SCRUM-334: the mobile row is a full-bleed 2-line CARD (design reservations.jsx:93 `<button all:unset>`); the kit <Button> is a padded pill/variant control that cannot host block card content — a reset <button> is the correct activatable wrapper (mirrors the design + the WAI-ARIA tab buttons in ReservationDetailView).
    <button
      type="button"
      data-reservation-id={row.id}
      onClick={() => onOpen?.(row.id)}
      aria-label={`Open reservation for ${row.guestDisplayName ?? 'guest with no name on file'}`}
      className="flex w-full cursor-pointer flex-col gap-2 rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-3.5 text-left"
    >
      {/* Line 1 — type badge + guest + mono id. */}
      <span className="flex items-center gap-2.5">
        <TypeIconBadge
          stayKind={glyphStayKindFor(row.glyphKind)}
          reservationTypeKey={row.reservationTypeKey}
          bookingChannel={bookingChannelFor(row.glyphKind)}
        />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-[color:var(--fg-primary)]">
            {row.guestDisplayName ?? 'No name on file'}
          </span>
          <span className="block truncate font-mono text-xs text-[color:var(--fg-tertiary)]">
            {row.id}
          </span>
        </span>
      </span>

      {/* Line 2 — status pill + terse money/date summary. */}
      <span className="flex flex-wrap items-center gap-2">
        <ResStatusPill status={row.status} modified={row.modified} />
        <span className="text-xs text-[color:var(--fg-tertiary)]">
          {`· ${fmtDate(row.arrivalDate)}–${fmtDate(row.departureDate)}`}
          {row.grandTotalCents !== null ? ` · ${formatCents(row.grandTotalCents, intl)}` : ''}
          {due !== null && due > 0 ? ` · ${formatCents(due, intl)} due` : ''}
        </span>
      </span>
    </button>
  );
}

/* ── glyph-kind → TypeIconBadge inputs ───────────────────────────────────── */

/**
 * The row view carries the pre-computed `glyphKind` bucket, but
 * `TypeIconBadge` re-derives it from `(stayKind, reservationTypeKey,
 * bookingChannel)`. Rather than plumb the raw wire triple through the view
 * (which deliberately exposes only `glyphKind`), we feed the badge the
 * canonical `stayKind` / `bookingChannel` inputs that reproduce the SAME
 * bucket via `glyphKindFor`. This keeps the badge's derivation the single
 * source of the glyph and avoids a second raw-field carrier on the view.
 */
function glyphStayKindFor(kind: ReservationRowView['glyphKind']): string {
  switch (kind) {
    case 'ownerBlock':
      return 'NonStayBlock';
    case 'ownerStay':
      return 'OwnerStay';
    case 'directGuest':
    case 'channelGuest':
      return 'PayingGuest';
    default:
      return '';
  }
}

/** A non-empty channel token for the `channelGuest` bucket only. */
function bookingChannelFor(kind: ReservationRowView['glyphKind']): string | null {
  return kind === 'channelGuest' ? 'channel' : null;
}
